import React, { useContext } from 'react';
import styles from '../styles/cr.module.scss';
import { IEntity, Entity, CustomFieldDfn, ICustomFieldDfn, EntityValidations, IEntityValidations, IUser, UserContext, IOrgLevel1, ListDefaults, IDirectorate, IOrgLevel3, IZCoreUserGroup, IZCoreLocation } from '../types';
import { EntityList } from '../components/EntityList';
import { UserRoleForm } from '../components/user/UserRoleForm';
import { UserForm } from '../components/user/UserForm';
import { ThresholdForm } from '../components/threshold/ThresholdForm';
import { ThresholdAppetiteForm } from '../components/threshold/ThresholdAppetiteForm';
import { UserProjectForm } from '../components/user/UserProjectForm';
import { UserPartnerOrganisationForm } from '../components/user/UserPartnerOrganisationForm';
import { EntityForm } from '../components/EntityForm';
import { UserDirectorateForm } from '../components/user/UserDirectorateForm';
import { UserGroupForm } from '../components/user/UserGroupForm';
import { RiskTypeForm } from '../components/risk/RiskTypeForm';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { CrTextField } from '../components/cr/CrTextField';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { EntityStatus } from '../refData/EntityStatus';
import { IUseApiProps } from '../components/useApi';
import { IWithErrorHandlingProps, withErrorHandling } from '../components/withErrorHandling';
import { ReportingFrequencyForm } from './reportingFrequency/ReportingFrequencyForm';
import { FinancialRiskUserGroupForm } from './user/FinancialRiskUserGroupForm';
import { ReportingEntityTypeForm } from './reportingEntities/ReportingEntityTypeForm';
import { DataContext } from './DataContext';
import { IntegrationContext } from './IntegrationContext';
import { EntityWithCustomer, IEntityWithCustomer } from '../types/Entity';
import { OrbUserContext } from './OrbUserContext';
import { GroupForm } from './group/GroupForm';
import { DirectorateFormAdmin } from './directorate/DirectorateFormAdmin';
import useMobileDetector from './useMobileDetector';
import { OrgLevel3Form } from './orgLevel3/OrgLevel3Form';
import { EmailTemplateForm } from './manageEmails/EmailTemplateForm';
import { ZCoreUserGroupForm } from './user/ZCoreUserGroupForm';
import { CrDropdown } from './cr/CrDropdown';
import { LookupService } from '../services';
import { CUSTOM_FIELD_SCOPES } from '../AppGlobals';
import { ZCoreLocationForm } from './zcoreLocation/ZCoreLocationForm';

export interface ISystemAdministrationProps extends IUseApiProps, IWithErrorHandlingProps {
	entity: string;
	menuData: any[];
}

const SystemAdministration = (props: ISystemAdministrationProps): React.ReactElement => {
	const { entity } = props;
	const { dataServices, loadLookupData } = useContext(DataContext);
	const { orbConfig } = useContext(OrbUserContext);
	const { disableUserManagement, disableGroupManagement, disableDirectorateManagement } = useContext(IntegrationContext);
	const lcc = ListDefaults.columnWidths;
	const isMobile = useMobileDetector();

	const userWidths = isMobile ? { minWidth: 100, maxWidth: 120 } : { minWidth: 150, maxWidth: 300 };
	const userTitleWidths = isMobile ? { minWidth: 80, maxWidth: 130 } : { minWidth: 80, maxWidth: 150 };
	const emailWidths = isMobile ? { minWidth: 80, maxWidth: 110 } : { minWidth: 80, maxWidth: 170 };
	const orgLevelNameWidths = isMobile ? { minWidth: 90, maxWidth: 170 } : { minWidth: 200, maxWidth: 350 };
	const customFieldNameWidths = isMobile ? { minWidth: 90, maxWidth: 170 } : { minWidth: 150, maxWidth: 300 };
	const riskTypeNameWidths = isMobile ? { minWidth: 130, maxWidth: 170 } : { minWidth: 200, maxWidth: 350 };
	const thresholdNameWidths = isMobile ? { minWidth: 150, maxWidth: 170 } : { minWidth: 200, maxWidth: 350 };
	const riskImpactLevelWidths = isMobile ? { minWidth: 100, maxWidth: 120 } : { minWidth: 150, maxWidth: 300 };

	//console.log('props.menuData', props.menuData);

	return (
		<ErrorBoundary>
			{entity === 'Groups' &&
				<EntityList<IOrgLevel1>
					{...props}
					disableAdd={disableGroupManagement}
					disableDelete={() => disableGroupManagement}
					enableShowHideClosedEntities={true}
					entityName={{ Plural: orbConfig.OrgL1LabelP, Singular: orbConfig.OrgL1Label }}
					columns={[
						{ key: '1', name: 'Name', fieldName: 'Name', ...orgLevelNameWidths, isResizable: true },
						{ key: '2', name: orbConfig.OrgMgrL1Lable, fieldName: 'DirectorGeneral', ...userWidths, isResizable: true, isCollapsible: true },
						{ key: '3', name: 'Status', fieldName: 'Status', minWidth: 100, isResizable: true, isCollapsible: true }
					]}
					loadListItems={showClosedEntities => dataServices.orgLevel1Service.readAllForList(showClosedEntities)}
					mapEntitiesToListItems={groups => groups.map(g => (
						{
							key: g.ID,
							Name: g.Title,
							DirectorGeneral: g.DirectorGeneralUser?.Title,
							Status: g.EntityStatus?.Title
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<GroupForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled} />
					}
					onCheckDelete={id => dataServices.orgLevel1Service.entityChildren(id)}
					onDelete={id => dataServices.orgLevel1Service.delete(id)}
				/>
			}
			{entity === 'Directorates' &&
				<EntityList<IDirectorate>
					{...props}
					disableAdd={disableDirectorateManagement}
					disableDelete={() => disableDirectorateManagement}
					enableShowHideClosedEntities={true}
					entityName={{ Plural: orbConfig.OrgL2LabelP, Singular: orbConfig.OrgL2Label }}
					columns={[
						{ key: '1', name: 'Name', fieldName: 'Name', ...orgLevelNameWidths, isResizable: true },
						{ key: '2', name: orbConfig.OrgL1Label, fieldName: 'Group', ...userWidths, isResizable: true, isCollapsible: true },
						{ key: '3', name: orbConfig.OrgMgrL2Lable, fieldName: 'Director', minWidth: 150, maxWidth: 250, isResizable: true, isCollapsible: true },
						{ key: '4', name: 'Status', fieldName: 'Status', minWidth: 100, isResizable: true, isCollapsible: true }
					]}
					loadListItems={showClosedEntities => dataServices.directorateService.readAllForList(showClosedEntities)}
					mapEntitiesToListItems={directorates => directorates.map(d => (
						{
							key: d.ID,
							Name: d.Title,
							Director: d.DirectorUser?.Title,
							AlternativeApprover: d.ReportApproverUser?.Title,
							Group: d.OrgLevel1?.Title,
							Status: d.EntityStatus?.Title,
							DirectorateStaff: [
								d.ReportApproverUser ? `ARA - ${d.ReportApproverUser.Title}` : null,
								d.ReportingLeadUser ? `RL - ${d.ReportingLeadUser.Title}` : null,
								d.Contributors && d.Contributors.some(c => c.ContributorUser != null) ? `HUC - ${d.Contributors.map(dc => dc.ContributorUser?.Title).join(', ')}` : null,
							].filter(Boolean).join(', '),
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<DirectorateFormAdmin
							{...props}
							entityName="Directorate"
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled}
						/>
					}
					onCheckDelete={id => dataServices.directorateService.entityChildren(id)}
					onDelete={id => dataServices.directorateService.delete(id)}
				/>
			}
			{entity === 'OrgLevel3s' &&
				<EntityList<IOrgLevel3>
					{...props}
					enableShowHideClosedEntities={true}
					entityName={{ Plural: orbConfig.OrgL3LabelP, Singular: orbConfig.OrgL3Label }}
					columns={[
						{ key: '1', name: 'Name', fieldName: 'Name', ...orgLevelNameWidths, isResizable: true },
						{ key: '2', name: orbConfig.OrgL2Label, fieldName: 'Directorate', ...userWidths, isResizable: true, isCollapsible: true },
						{ key: '3', name: orbConfig.OrgMgrL3Lable, fieldName: 'DeputyDirector', minWidth: 150, maxWidth: 250, isResizable: true, isCollapsible: true },
						{ key: '4', name: 'Status', fieldName: 'Status', minWidth: 100, isResizable: true, isCollapsible: true }
					]}
					loadListItems={showClosedEntities => dataServices.orgLevel3Service.readAllForList(showClosedEntities)}
					mapEntitiesToListItems={groups => groups.map(l3 => (
						{
							key: l3.ID,
							Name: l3.Title,
							Directorate: l3.Directorate?.Title,
							DeputyDirector: l3.DeputyDirectorUser?.Title,
							Status: l3.EntityStatus?.Title
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<OrgLevel3Form
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled} />
					}
					onCheckDelete={id => dataServices.orgLevel3Service.entityChildren(id)}
					onDelete={id => dataServices.orgLevel3Service.delete(id)}
				/>
			}
			{entity === 'ZCoreLocations' &&
				<EntityList<IZCoreLocation>
					{...props}
					enableShowHideClosedEntities={true}
					entityName={{ Plural: 'Locations', Singular: 'Location' }}
					columns={[
						{ key: '1', name: 'Title', fieldName: 'Title', minWidth: 100, maxWidth: 150, isResizable: true },
						{ key: '2', name: 'City', fieldName: 'City', minWidth: 80, maxWidth: 120, isResizable: true, isCollapsible: true },
						{ key: '3', name: 'State', fieldName: 'StateRegion', minWidth: 80, maxWidth: 120, isResizable: true, isCollapsible: true },
						{ key: '4', name: 'Country', fieldName: 'Country', minWidth: 80, maxWidth: 120, isResizable: true, isCollapsible: true }
					]}
					loadListItems={showClosedEntities => dataServices.zCoreLocationService.readAllForList(showClosedEntities)}
					mapEntitiesToListItems={locations => locations.map(l => (
						{
							key: l.ID,
							Title: l.Title,
							City: l.City,
							StateRegion: l.StateRegion,
							Country: l.Country
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<ZCoreLocationForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled} />
					}
					onCheckDelete={id => dataServices.zCoreLocationService.entityChildren(id)}
					onDelete={id => dataServices.zCoreLocationService.delete(id)}
				/>
			}
			{entity === 'Administrators' &&
				<EntityList
					{...props}
					entityName={{ Plural: 'User Permissions', Singular: 'User Permission' }}
					columns={[
						{ key: '1', name: 'User', fieldName: 'User', ...userWidths, isResizable: true },
						{ key: '2', name: 'Permissions', fieldName: 'Role', minWidth: 100, isResizable: true, isCollapsible: true }
					]}
					loadListItems={() => dataServices.userRoleService.readAllForList2()}
					mapEntitiesToListItems={userRoles => userRoles.map(ur => (
						{
							// key: ur.ID,
							// User: ur.User?.Title,
							// Role: ur.Role?.Title
							key: ur['id'],
							User: ur['userTitle'],
							Role: ur['roleTitle']
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<UserRoleForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled} />
					}
					onCheckDelete={() => Promise.resolve([])}
					onDelete={id => dataServices.userRoleService.delete(id)}
				/>
			}
			{entity === 'CustomFields' &&
				<EntityList
					{...props}
					entityName={{ Plural: "Custom fields", Singular: "Custom field" }}
					columns={[
						{ key: '1', name: 'Custom field name', fieldName: 'Name', ...customFieldNameWidths, isResizable: true },
						{ key: '2', name: 'Data Type', fieldName: 'DataType', minWidth: 60, maxWidth: 80, isResizable: true, isCollapsible: true },
						{ key: '3', name: 'Scope', fieldName: 'Scope', minWidth: 120, maxWidth: 200, isResizable: true, isCollapsible: true },
						{ key: '4', name: 'To be displayed', fieldName: 'Display', minWidth: 150, isResizable: true, isCollapsible: true }
					]}
					loadListItems={() => dataServices.customFieldDfnService.readAllForList()}
					mapEntitiesToListItems={customFieldDfns => customFieldDfns.map(e => (
						{
							key: e.ID,
							Name: e.Title,
							DataType: e.DataType,
							Scope: e.Scope,
							Display: e.Display ? "Yes" : "No"
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<EntityForm<ICustomFieldDfn, IEntityValidations>
							{...props}
							renderFormFields={(changeHandlers, formState) =>
								<div>
									<CrTextField
										label="Name"
										className={styles.formField}
										required={true}
										maxLength={255}
										value={formState.FormData.Title}
										onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Title')}
										errorMessage={formState.ValidationErrors.Title} />
									<CrDropdown
										label="Scope"
										className={styles.formField}
										required
										options={LookupService.entitiesToSelectableOptionsCustom(
											CUSTOM_FIELD_SCOPES.map(s => ({
												ID: s,
												Title: s,
											}))
										)}
										selectedKey={formState.FormData.Scope}
										onChange={(_, o) => changeHandlers.changeDropdown(o, 'Scope')}
									/>

									<CrDropdown
										label="Data Type"
										className={styles.formField}
										options={LookupService.entitiesToSelectableOptionsCustom([
											{ ID: 'Text', Title: 'Text' },
											{ ID: 'List', Title: 'List' },
											{ ID: 'MultiSelectList', Title: 'MultiSelectList' },
											{ ID: 'Date', Title: 'Date' },
											{ ID: 'DateRange', Title: 'DateRange' },
										])}
										selectedKey={formState.FormData.DataType}
										onChange={(_, o) => changeHandlers.changeDropdown(o, 'DataType')}
									/>

									{(formState.FormData.DataType === 'List' || formState.FormData.DataType === 'MultiSelectList') && <CrTextField
										label="List Values (Comma Separated)"
										className={styles.formField}
										maxLength={2000}
										value={formState.FormData.ListValues}
										onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'ListValues')}
									/>}

									<Checkbox
										className={`${styles.formField} ${styles.formField}`}
										label="Display as Tag?"
										checked={formState.FormData.Display}
										onChange={(_e, isChecked) => changeHandlers.changeCheckbox(isChecked, 'Display')} />
								</div>
							}
							entityName="Custom field"
							loadEntity={id => dataServices.customFieldDfnService.read(id, true, true)}
							loadNewEntity={() => new CustomFieldDfn()}
							loadEntityValidations={() => new EntityValidations()}
							onCreate={at => dataServices.customFieldDfnService.create(at)}
							onUpdate={at => dataServices.customFieldDfnService.update(at.ID, at)}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled}
						/>
					}
					onCheckDelete={id => dataServices.customFieldDfnService.entityChildren(id)}
					onDelete={id => dataServices.customFieldDfnService.delete(id)}
				/>
			}
			{entity === 'BenefitTypes' &&
				<EntityList
					{...props}
					entityName={{ Plural: "Benefit types", Singular: "Benefit type" }}
					columns={[
						{ key: '1', name: 'Name', fieldName: 'Name', minWidth: 200, isResizable: true }
					]}
					loadListItems={() => dataServices.benefitTypeService.readAllForList()}
					mapEntitiesToListItems={benefitTypes => benefitTypes.map(e => (
						{
							key: e.ID,
							Name: e.Title
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<EntityForm<IEntityWithCustomer, IEntityValidations>
							{...props}
							entityName="Benefit type"
							loadEntity={id => dataServices.benefitTypeService.read(id, true, true)}
							loadNewEntity={() => new EntityWithCustomer()}
							loadEntityValidations={() => new EntityValidations()}
							onCreate={bt => dataServices.benefitTypeService.create(bt)}
							onUpdate={bt => dataServices.benefitTypeService.update(bt.ID, bt)}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled}
						/>
					}
					onChange={() => loadLookupData.benefitTypes(true)}
					onCheckDelete={id => dataServices.benefitTypeService.entityChildren(id)}
					onDelete={id => dataServices.benefitTypeService.delete(id)}
				/>
			}
			{entity === 'MilestoneTypes' &&
				<EntityList
					{...props}
					entityName={{ Plural: "Milestone types", Singular: "Milestone type" }}
					columns={[
						{ key: '1', name: 'Name', fieldName: 'Name', minWidth: 550, isResizable: true }
					]}
					loadListItems={() => dataServices.milestoneTypeService.readAllForList()}
					mapEntitiesToListItems={milestoneTypes => milestoneTypes.map(mt => (
						{
							key: mt.ID,
							Name: mt.Title
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<EntityForm<IEntity, IEntityValidations>
							{...props}
							entityName="Milestone type"
							loadEntity={id => dataServices.milestoneTypeService.read(id, true, true)}
							loadNewEntity={() => new EntityWithCustomer()}
							loadEntityValidations={() => new EntityValidations()}
							onCreate={mt => dataServices.milestoneTypeService.create(mt)}
							onUpdate={mt => dataServices.milestoneTypeService.update(mt.ID, mt)}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled}
						/>
					}
					onChange={() => loadLookupData.milestoneTypes(true)}
					onCheckDelete={id => dataServices.milestoneTypeService.entityChildren(id)}
					onDelete={id => dataServices.milestoneTypeService.delete(id)}
				/>
			}
			{entity === 'ReportingFrequencies' &&
				<EntityList
					{...props}
					disableAdd={true}
					disableDelete={() => true}
					entityName={{ Plural: "Reporting frequencies", Singular: "Reporting frequency" }}
					columns={[
						{ key: '1', name: 'Name', fieldName: 'Name', minWidth: 200, maxWidth: 300, isResizable: true },
						{ key: '2', name: 'Days before due to remind authors', fieldName: 'RemindAuthorsDaysBeforeDue', minWidth: 200, maxWidth: 400, isResizable: true, isCollapsible: true },
						{ key: '3', name: 'Days before due to remind approvers', fieldName: 'RemindApproverDaysBeforeDue', minWidth: 200, maxWidth: 400, isResizable: true, isCollapsible: true },
						{ key: '4', name: 'Days for early update warning', fieldName: 'EarlyUpdateWarningDays', minWidth: 200, isResizable: true, isCollapsible: true }
					]}
					loadListItems={() => dataServices.reportingFrequencyService.readAllForList()}
					mapEntitiesToListItems={reportingFrequencies => reportingFrequencies.map(e => (
						{
							key: e.ID,
							Name: e.Title,
							RemindAuthorsDaysBeforeDue: e.RemindAuthorsDaysBeforeDue,
							RemindApproverDaysBeforeDue: e.RemindApproverDaysBeforeDue,
							EarlyUpdateWarningDays: e.EarlyUpdateWarningDays
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<ReportingFrequencyForm
							{...props}
							entityName="Reporting frequency"
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled}
						/>
					}
					onCheckDelete={() => Promise.resolve([])}
					onDelete={id => dataServices.reportingFrequencyService.delete(id)}
				/>
			}
			{entity === 'RiskTypes' &&
				<EntityList
					{...props}
					entityName={{ Plural: "Risk types", Singular: "Risk type" }}
					columns={[
						{ key: '1', name: 'Name', fieldName: 'Name', ...riskTypeNameWidths, isResizable: true },
						{ key: '2', name: 'Threshold', fieldName: 'Threshold', minWidth: 100, isResizable: true, isCollapsible: true },
					]}
					loadListItems={() => dataServices.riskTypeService.readAllForList()}
					mapEntitiesToListItems={riskTypes => riskTypes.map(rt => (
						{
							key: rt.ID,
							Name: rt.Title,
							Threshold: rt.Threshold?.Title
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<RiskTypeForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled} />
					}
					onChange={() => loadLookupData.riskTypes(true)}
					onCheckDelete={id => dataServices.riskTypeService.entityChildren(id)}
					onDelete={id => dataServices.riskTypeService.delete(id)}
				/>
			}
			{entity === 'RiskDiscussionForums' &&
				<EntityList
					{...props}
					entityName={{ Plural: "Risk discussion forums", Singular: "Risk discussion forum" }}
					columns={[
						{ key: '1', name: 'Name', fieldName: 'Name', minWidth: 200, isResizable: true }
					]}
					loadListItems={() => dataServices.riskDiscussionForumService.readAllForList()}
					mapEntitiesToListItems={forum => forum.map(f => (
						{
							key: f.ID,
							Name: f.Title
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<EntityForm<IEntityWithCustomer, IEntityValidations>
							{...props}
							entityName="Risk discussion forum"
							loadEntity={id => dataServices.riskDiscussionForumService.read(id, true, true)}
							loadNewEntity={() => new EntityWithCustomer()}
							loadEntityValidations={() => new EntityValidations()}
							onCreate={at => dataServices.riskDiscussionForumService.create(at)}
							onUpdate={at => dataServices.riskDiscussionForumService.update(at.ID, at)}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled}
						/>
					}
					onChange={() => loadLookupData.riskDiscussionForums(true)}
					onCheckDelete={id => dataServices.riskTypeService.entityChildren(id)}
					onDelete={id => dataServices.riskTypeService.delete(id)}
				/>
			}
			{entity === 'Units' &&
				<EntityList
					{...props}
					entityName={{ Plural: "Units of measurement", Singular: "Unit of measurement" }}
					columns={[
						{ key: '1', name: 'Name', fieldName: 'Name', minWidth: 200, isResizable: true }
					]}
					loadListItems={() => dataServices.measurementUnitService.readAllForList()}
					mapEntitiesToListItems={(entities: IEntity[]) => entities.map(u => (
						{
							key: u.ID,
							Name: u.Title
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<EntityForm<IEntityWithCustomer, IEntityValidations>
							{...props}
							entityName="Unit of measurement"
							loadEntity={id => dataServices.measurementUnitService.read(id, true, true)}
							loadNewEntity={() => new EntityWithCustomer()}
							loadEntityValidations={() => new EntityValidations()}
							onCreate={at => dataServices.measurementUnitService.create(at)}
							onUpdate={at => dataServices.measurementUnitService.update(at.ID, at)}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled}
						/>
					}
					onChange={() => loadLookupData.measurementUnits(true)}
					onCheckDelete={id => dataServices.measurementUnitService.entityChildren(id)}
					onDelete={id => dataServices.measurementUnitService.delete(id)}
				/>
			}
			{entity === 'UserDirectorates' &&
				<EntityList
					{...props}
					entityName={{ Plural: 'User directorates', Singular: 'User directorate' }}
					columns={[
						{ key: '1', name: 'User', fieldName: 'User', ...userWidths, isResizable: true },
						{ key: '2', name: orbConfig.OrgL2Label, fieldName: 'Directorate', minWidth: 150, isResizable: true },
						{ key: '3', name: 'User is admin?', fieldName: 'IsAdmin', minWidth: 150, isResizable: true },
						{ key: '4', name: 'User is risk admin?', fieldName: 'IsRiskAdmin', minWidth: 150, isResizable: true }
					]}
					loadListItems={() => dataServices.userDirectorateService.readAllForList()}
					mapEntitiesToListItems={userDirectorates => userDirectorates.map(ud => (
						{
							key: ud.ID,
							User: ud.User?.Title,
							Directorate: ud.Directorate?.Title,
							IsAdmin: ud.IsAdmin ? 'Yes' : 'No',
							IsRiskAdmin: ud.IsRiskAdmin ? 'Yes' : 'No'
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<UserDirectorateForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled} />
					}
					onCheckDelete={() => Promise.resolve([])}
					onDelete={id => dataServices.userDirectorateService.delete(id)}
				/>
			}
			{entity === 'UserGroups' &&
				<EntityList
					{...props}
					entityName={{ Plural: 'User groups', Singular: 'User group' }}
					columns={[
						{ key: '1', name: 'User', fieldName: 'User', ...userWidths, isResizable: true },
						{ key: '2', name: orbConfig.OrgL1Label, fieldName: 'Group', minWidth: 150, isResizable: true, isCollapsible: true }
					]}
					loadListItems={() => dataServices.userGroupService.readAllForList()}
					mapEntitiesToListItems={userGroups => userGroups.map(ug => (
						{
							key: ug.ID,
							User: ug.User?.Title,
							Group: ug.OrgLevel1?.Title
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<UserGroupForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled} />
					}
					onCheckDelete={() => Promise.resolve([])}
					onDelete={id => dataServices.userGroupService.delete(id)}
				/>
			}
			{entity === 'FinancialRiskUserGroups' &&
				<EntityList
					{...props}
					entityName={{ Plural: 'Financial risk user groups', Singular: 'Financial risk user group' }}
					columns={[
						{ key: '1', name: 'User', fieldName: 'User', ...userWidths, isResizable: true },
						{ key: '2', name: 'Group', fieldName: 'Group', minWidth: 150, isResizable: true }
					]}
					loadListItems={() => dataServices.financialRiskUserGroupService.readAllForList()}
					mapEntitiesToListItems={userGroups => userGroups.map(ug => (
						{
							key: ug.ID,
							User: ug.User?.Title,
							Group: ug.OrgLevel1?.Title
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<FinancialRiskUserGroupForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled}
						/>
					}
					onCheckDelete={() => Promise.resolve([])}
					onDelete={id => dataServices.financialRiskUserGroupService.delete(id)}
				/>
			}
			{entity === 'UserPartnerOrganisations' &&
				<EntityList
					{...props}
					entityName={{ Plural: 'User partner organisations', Singular: 'User partner organisation' }}
					columns={[
						{ key: '1', name: 'User', fieldName: 'User', ...userWidths, isResizable: true },
						{ key: '2', name: 'Partner organisation', fieldName: 'PartnerOrganisation', minWidth: 150, isResizable: true },
						{ key: '3', name: 'User is admin?', fieldName: 'IsAdmin', minWidth: 150, isResizable: true }
					]}
					loadListItems={() => dataServices.userPartnerOrganisationService.readAllForList()}
					mapEntitiesToListItems={userPartnerOrganisations => userPartnerOrganisations.map(upo => (
						{
							key: upo.ID,
							User: upo.User?.Title,
							PartnerOrganisation: upo.PartnerOrganisation?.Title,
							IsAdmin: upo.IsAdmin ? 'Yes' : 'No'
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<UserPartnerOrganisationForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled} />
					}
					onCheckDelete={() => Promise.resolve([])}
					onDelete={id => dataServices.userPartnerOrganisationService.delete(id)}
				/>
			}
			{entity === 'UserProjects' &&
				<EntityList
					{...props}
					entityName={{ Plural: 'User projects', Singular: 'User project' }}
					columns={[
						{ key: '1', name: 'User', fieldName: 'User', ...userWidths, isResizable: true },
						{ key: '2', name: 'Project', fieldName: 'Project', minWidth: 150, isResizable: true },
						{ key: '3', name: 'User is admin?', fieldName: 'IsAdmin', minWidth: 150, isResizable: true }
					]}
					loadListItems={() => dataServices.userProjectService.readAllForList()}
					mapEntitiesToListItems={userProjects => userProjects.map(up => (
						{
							key: up.ID,
							User: up.User?.Title,
							Project: up.Project?.Title,
							IsAdmin: up.IsAdmin ? 'Yes' : 'No'
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<UserProjectForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled} />
					}
					onCheckDelete={() => Promise.resolve([])}
					onDelete={id => dataServices.userProjectService.delete(id)}
				/>
			}
			{entity === 'Users' &&
				<EntityList<IUser>
					{...props}
					disableDelete={u => disableUserManagement && !u?.IsServiceAccount}
					enableShowHideClosedEntities={true}
					entityName={{ Plural: 'Users', Singular: 'User' }}
					columns={[
						{ key: '1', name: 'Display name', fieldName: 'DisplayName', ...userTitleWidths, isResizable: true },
						{ key: '3', name: 'Job Role', fieldName: 'JobRole', minWidth: 80, maxWidth: 150, isResizable: true, },
						{ key: '2', name: 'Email address', fieldName: 'EmailAddress', ...emailWidths, isResizable: true, isCollapsible: true },
						{ key: '4', name: 'User Groups', fieldName: 'UserGroups', minWidth: 150, maxWidth: 250, isResizable: true, isCollapsible: true, isMultiline: true },
						{ key: '5', name: 'Enabled?', fieldName: 'Enabled', minWidth: 100, isResizable: true, isCollapsible: true }
					]}
					loadListItems={showClosedEntities => dataServices.userService.readAllForList(showClosedEntities)}
					mapEntitiesToListItems={users => users.map(u => ({
						key: u.ID,
						Username: u.Username,
						DisplayName: u.Title,
						EmailAddress: u.EmailAddress,
						JobRole: u.JobRole ?? '',
						Enabled: u.EntityStatusID === EntityStatus.Open ? 'Yes' : 'No',
						UserGroups: [
							u.ZCoreUserGroupMembers && u.ZCoreUserGroupMembers.some(gm => gm.ZCoreUserGroup != null) ? `${u.ZCoreUserGroupMembers.map(gm => gm.ZCoreUserGroup?.Title).join(', ')}` : null,
						].filter(Boolean).join(', '),
					}))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<UserForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled} />
					}
					onCheckDelete={id => dataServices.userService.entityChildren(id)}
					onDelete={id => dataServices.userService.delete(id)}
				/>
			}

			{entity === 'ZCoreUserGroups' &&
				<EntityList<IZCoreUserGroup>
					{...props}
					entityName={{ Plural: 'User Groups', Singular: 'User Group' }}
					columns={[
						{ key: '1', name: 'Group', fieldName: 'GroupName', ...userWidths, isResizable: true, isCollapsible: true },
					]}
					loadListItems={showClosedEntities => dataServices.zCoreUserGroupService.readAllForList(showClosedEntities)}
					mapEntitiesToListItems={userGroups => userGroups.map(userGroup => ({
						key: userGroup.ID,
						GroupName: userGroup.Title
					}))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<ZCoreUserGroupForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled} />
					}
					onCheckDelete={id => dataServices.zCoreUserGroupService.entityChildren(id)}
					onDelete={id => dataServices.zCoreUserGroupService.delete(id)}
				/>
			}

			{entity === 'Thresholds' &&
				<EntityList
					{...props}
					entityName={{ Plural: 'Thresholds', Singular: 'Threshold' }}
					columns={[
						{ key: '1', name: 'Threshold Name', fieldName: 'Name', ...thresholdNameWidths, isResizable: true },
						{ key: '2', name: 'Priority', fieldName: 'Priority', minWidth: 80, isResizable: true, isCollapsible: true }
					]}
					loadListItems={() => dataServices.thresholdService.readAllForList()}
					mapEntitiesToListItems={thresholds => thresholds.map(e => (
						{
							key: e.ID,
							Name: e.Title,
							Priority: e.Priority
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<ThresholdForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled} />
					}
					onChange={() => loadLookupData.thresholds(true)}
					onCheckDelete={id => dataServices.thresholdService.entityChildren(id)}
					onDelete={id => dataServices.thresholdService.delete(id)}
				/>
			}
			{entity === 'ThresholdAppetites' &&
				<EntityList
					{...props}
					entityName={{ Plural: 'Threshold Appetites', Singular: 'Threshold Appetite' }}
					columns={[
						{ key: '1', name: 'Threshold Appetite Name', fieldName: 'Name', ...thresholdNameWidths, isResizable: true },
						{ key: '2', name: 'Risk Impact Level', fieldName: 'ImpactLevel', ...riskImpactLevelWidths, isResizable: true, isCollapsible: true },
						{ key: '3', name: 'Risk Probability', fieldName: 'Probability', minWidth: 150, maxWidth: 300, isResizable: true, isCollapsible: true },
						{ key: '4', name: 'Within risk appetite boundary', fieldName: 'Acceptable', minWidth: 150, isCollapsible: true }
					]}
					loadListItems={() => dataServices.thresholdAppetiteService.readAllForList()}
					mapEntitiesToListItems={thresholdAppetites => thresholdAppetites.map(ta => (
						{
							key: ta.ID,
							Name: ta.Threshold?.Title,
							Probability: ta.RiskProbability?.Title,
							ImpactLevel: ta.RiskImpactLevel?.Title,
							Acceptable: ta.Acceptable ? 'Yes' : 'No',
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<ThresholdAppetiteForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled} />
					}
					onChange={() => loadLookupData.thresholdAppetites(true)}
					onCheckDelete={() => Promise.resolve([])}
					onDelete={id => dataServices.thresholdAppetiteService.delete(id)}
				/>
			}
			{entity === 'CustomReportSections' &&
				<EntityList
					{...props}
					entityName={{ Plural: 'Custom report sections', Singular: 'Custom report section' }}
					columns={[
						{ key: '1', name: 'Name', fieldName: 'Title', minWidth: 300, isResizable: true },
						{ key: '2', name: 'Report', fieldName: 'Report', minWidth: 150, isResizable: true }
					]}
					loadListItems={() => dataServices.reportingEntityTypeService.readAllForList()}
					mapEntitiesToListItems={reportingEntityTypes => reportingEntityTypes.map(ret => (
						{
							key: ret.ID,
							Title: ret.Title,
							Report: ret.ReportType?.Title
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<ReportingEntityTypeForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled}
						/>
					}
					onCheckDelete={() => Promise.resolve([])}
					onDelete={id => dataServices.reportingEntityTypeService.delete(id)}
				/>
			}
			{entity === 'EmailTemplates' &&
				<EntityList
					{...props}
					entityName={{ Plural: 'Email Templates', Singular: 'Email Template' }}
					columns={[
						{ key: '1', name: 'Name', fieldName: 'Title', ...userWidths, isResizable: true },
						// { key: '2', name: 'Permissions', fieldName: 'Role', minWidth: 100, isResizable: true, isCollapsible: true }
					]}
					loadListItems={() => dataServices.zCoreEmailTemplateService.readAllForList()}
					mapEntitiesToListItems={x => x.map(x => (
						{
							key: x.ID,
							Title: x.Title,
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<EmailTemplateForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled} />
					}
					onCheckDelete={() => Promise.resolve([])}
					onDelete={id => dataServices.zCoreEmailOutboxService.delete(id)}
				/>
			}
		</ErrorBoundary>
	);
};

export default withErrorHandling(SystemAdministration);
